// SignPage — /sign/:token — multi-signer signature flow
// Werkt met /api/forms-workflow/public/:token endpoints
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf-worker/pdf.worker.min.js';

const RENDER_SCALE = 1.5;
const API = '/api/forms-workflow';

// FORMS_I18N — NL/EN teksten tekenpagina
const T = {
  nl: {
    loading: 'Document laden…', invalid: 'Link ongeldig of verlopen', done: 'Klaar',
    st_verlopen: 'Ondertekentermijn verstreken', st_beurt: 'Nog niet aan de beurt', st_compleet: 'Document is volledig ondertekend',
    st_reeds: 'Je hebt al getekend', st_geweigerd: 'Document is geweigerd', st_reeds_gew: 'Je hebt deze ondertekening geweigerd',
    hi: 'Hi', intro: 'open dit document, controleer de inhoud en zet je handtekening onderaan.', verzoek: 'Verzoek van',
    pagina: 'Pagina', akkoord: 'Ik heb dit document gelezen en ga akkoord met de inhoud en met', akkoord2: 'elektronisch ondertekenen',
    akkoord3: '. Mijn naam, e-mail, IP-adres en tijdstip worden vastgelegd in het certificaat van voltooiing.',
    vink: 'Vink eerst akkoord aan', weigeren: '✗ Weigeren', ondertekenen: '✍ Ondertekenen', handtekening: 'Handtekening',
    m_title: 'Ondertekenen', m_sub: 'teken hieronder met vinger of muis', m_naam: 'Je naam (zoals deze op de PDF komt)', m_ph: 'Bijv. Jan Janssen',
    wissen: 'Wissen', annuleren: 'Annuleren', bezig: 'Bezig…', opslaan: 'Opslaan & versturen', eerst: 'Zet eerst een handtekening',
    naam_req: 'Vul je naam in zoals je op de PDF wil staan.', teken_fout: 'Tekenen mislukt: ',
    w_title: 'Document weigeren', w_sub: 'Geef kort aan waarom je dit document niet wil ondertekenen.', w_ph: 'Bijv. de prijs klopt niet…', w_btn: 'Weigeren',
    w_done: 'De ondertekening is geweigerd. De afzender wordt op de hoogte gebracht.', w_fout: 'Weigeren mislukt: ',
    s_done_all: '✅ Bedankt — alle ondertekenaars hebben nu getekend, het document is compleet.', s_done: '✅ Bedankt — je handtekening is opgeslagen. Andere ondertekenaars worden afgewacht.',
    otp_title: 'SMS-verificatie', otp_sub: 'Geavanceerde handtekening · SMS-code', otp_p: 'Dit document vereist een verificatie via SMS. We sturen een 6-cijferige code naar je telefoon. Laat het veld leeg om je opgeslagen nummer te gebruiken.',
    otp_tel: 'Telefoonnummer', otp_tel_ph: '(leeg = opgeslagen nummer)', otp_saved: 'We sturen SMS naar opgeslagen nummer:', otp_send: '📩 Verstuur SMS code', otp_sending: 'Versturen…',
    otp_code: '6-cijferige code', otp_verify: '✅ Verifieer & teken', otp_verifying: 'Verifieren…', otp_sent: 'SMS verstuurd naar', otp_6: 'Voer de 6-cijferige code in', fout: 'Fout: '
  },
  en: {
    loading: 'Loading document…', invalid: 'Link invalid or expired', done: 'Done',
    st_verlopen: 'Signing period has expired', st_beurt: 'Not your turn yet', st_compleet: 'Document is fully signed',
    st_reeds: 'You have already signed', st_geweigerd: 'Document was declined', st_reeds_gew: 'You declined this signature request',
    hi: 'Hi', intro: 'please open this document, review the content and sign at the bottom.', verzoek: 'Request from',
    pagina: 'Page', akkoord: 'I have read this document and agree with its content and with', akkoord2: 'electronic signing',
    akkoord3: '. My name, e-mail, IP address and timestamp will be recorded in the certificate of completion.',
    vink: 'Please tick the consent box first', weigeren: '✗ Decline', ondertekenen: '✍ Sign', handtekening: 'Signature',
    m_title: 'Sign', m_sub: 'draw your signature below with finger or mouse', m_naam: 'Your name (as it will appear on the PDF)', m_ph: 'e.g. John Smith',
    wissen: 'Clear', annuleren: 'Cancel', bezig: 'Working…', opslaan: 'Save & send', eerst: 'Please draw your signature first',
    naam_req: 'Enter your name as it should appear on the PDF.', teken_fout: 'Signing failed: ',
    w_title: 'Decline document', w_sub: 'Briefly state why you do not want to sign this document.', w_ph: 'e.g. the price is incorrect…', w_btn: 'Decline',
    w_done: 'You declined to sign. The sender has been notified.', w_fout: 'Decline failed: ',
    s_done_all: '✅ Thank you — all parties have signed, the document is complete.', s_done: '✅ Thank you — your signature has been saved. Waiting for the other signers.',
    otp_title: 'SMS verification', otp_sub: 'Advanced signature · SMS code', otp_p: 'This document requires SMS verification. We will send a 6-digit code to your phone. Leave the field empty to use the number on file.',
    otp_tel: 'Phone number', otp_tel_ph: '(empty = number on file)', otp_saved: 'We will text the number on file:', otp_send: '📩 Send SMS code', otp_sending: 'Sending…',
    otp_code: '6-digit code', otp_verify: '✅ Verify & sign', otp_verifying: 'Verifying…', otp_sent: 'SMS sent to', otp_6: 'Enter the 6-digit code', fout: 'Error: '
  },
  de: {
    loading: 'Dokument wird geladen…', invalid: 'Link ungültig oder abgelaufen', done: 'Fertig',
    st_verlopen: 'Unterzeichnungsfrist abgelaufen', st_beurt: 'Sie sind noch nicht an der Reihe', st_compleet: 'Dokument ist vollständig unterzeichnet',
    st_reeds: 'Sie haben bereits unterzeichnet', st_geweigerd: 'Dokument wurde abgelehnt', st_reeds_gew: 'Sie haben diese Unterzeichnung abgelehnt',
    hi: 'Hallo', intro: 'bitte öffnen Sie dieses Dokument, prüfen Sie den Inhalt und unterschreiben Sie unten.', verzoek: 'Anfrage von',
    pagina: 'Seite', akkoord: 'Ich habe dieses Dokument gelesen und stimme dem Inhalt sowie der', akkoord2: 'elektronischen Unterzeichnung',
    akkoord3: ' zu. Name, E-Mail, IP-Adresse und Zeitpunkt werden im Abschlusszertifikat festgehalten.',
    vink: 'Bitte zuerst die Zustimmung ankreuzen', weigeren: '✗ Ablehnen', ondertekenen: '✍ Unterschreiben', handtekening: 'Unterschrift',
    m_title: 'Unterschreiben', m_sub: 'unterschreiben Sie unten mit Finger oder Maus', m_naam: 'Ihr Name (wie er im PDF erscheint)', m_ph: 'z. B. Max Mustermann',
    wissen: 'Löschen', annuleren: 'Abbrechen', bezig: 'Bitte warten…', opslaan: 'Speichern & senden', eerst: 'Bitte zuerst unterschreiben',
    naam_req: 'Geben Sie Ihren Namen ein, wie er im PDF erscheinen soll.', teken_fout: 'Unterzeichnung fehlgeschlagen: ',
    w_title: 'Dokument ablehnen', w_sub: 'Bitte kurz angeben, warum Sie dieses Dokument nicht unterzeichnen möchten.', w_ph: 'z. B. der Preis stimmt nicht…', w_btn: 'Ablehnen',
    w_done: 'Sie haben die Unterzeichnung abgelehnt. Der Absender wurde informiert.', w_fout: 'Ablehnen fehlgeschlagen: ',
    s_done_all: '✅ Vielen Dank — alle Parteien haben unterschrieben, das Dokument ist vollständig.', s_done: '✅ Vielen Dank — Ihre Unterschrift wurde gespeichert. Wir warten auf die anderen Unterzeichner.',
    otp_title: 'SMS-Verifizierung', otp_sub: 'Fortgeschrittene Signatur · SMS-Code', otp_p: 'Dieses Dokument erfordert eine SMS-Verifizierung. Wir senden einen 6-stelligen Code an Ihr Telefon. Feld leer lassen, um die hinterlegte Nummer zu verwenden.',
    otp_tel: 'Telefonnummer', otp_tel_ph: '(leer = hinterlegte Nummer)', otp_saved: 'SMS geht an die hinterlegte Nummer:', otp_send: '📩 SMS-Code senden', otp_sending: 'Wird gesendet…',
    otp_code: '6-stelliger Code', otp_verify: '✅ Prüfen & unterschreiben', otp_verifying: 'Wird geprüft…', otp_sent: 'SMS gesendet an', otp_6: 'Bitte den 6-stelligen Code eingeben', fout: 'Fehler: '
  },
  fr: {
    loading: 'Chargement du document…', invalid: 'Lien invalide ou expiré', done: 'Terminé',
    st_verlopen: 'Délai de signature expiré', st_beurt: 'Ce n’est pas encore votre tour', st_compleet: 'Le document est entièrement signé',
    st_reeds: 'Vous avez déjà signé', st_geweigerd: 'Le document a été refusé', st_reeds_gew: 'Vous avez refusé cette signature',
    hi: 'Bonjour', intro: 'veuillez ouvrir ce document, vérifier son contenu et signer en bas.', verzoek: 'Demande de',
    pagina: 'Page', akkoord: 'J’ai lu ce document et j’accepte son contenu ainsi que la', akkoord2: 'signature électronique',
    akkoord3: '. Mon nom, mon e-mail, mon adresse IP et l’horodatage seront consignés dans le certificat d’achèvement.',
    vink: 'Veuillez d’abord cocher la case de consentement', weigeren: '✗ Refuser', ondertekenen: '✍ Signer', handtekening: 'Signature',
    m_title: 'Signer', m_sub: 'signez ci-dessous avec le doigt ou la souris', m_naam: 'Votre nom (tel qu’il apparaîtra sur le PDF)', m_ph: 'p. ex. Jean Dupont',
    wissen: 'Effacer', annuleren: 'Annuler', bezig: 'Patientez…', opslaan: 'Enregistrer & envoyer', eerst: 'Veuillez d’abord signer',
    naam_req: 'Saisissez votre nom tel qu’il doit apparaître sur le PDF.', teken_fout: 'Échec de la signature : ',
    w_title: 'Refuser le document', w_sub: 'Indiquez brièvement pourquoi vous ne souhaitez pas signer ce document.', w_ph: 'p. ex. le prix est incorrect…', w_btn: 'Refuser',
    w_done: 'Vous avez refusé de signer. L’expéditeur a été informé.', w_fout: 'Échec du refus : ',
    s_done_all: '✅ Merci — toutes les parties ont signé, le document est complet.', s_done: '✅ Merci — votre signature a été enregistrée. En attente des autres signataires.',
    otp_title: 'Vérification par SMS', otp_sub: 'Signature avancée · code SMS', otp_p: 'Ce document requiert une vérification par SMS. Nous enverrons un code à 6 chiffres sur votre téléphone. Laissez le champ vide pour utiliser le numéro enregistré.',
    otp_tel: 'Numéro de téléphone', otp_tel_ph: '(vide = numéro enregistré)', otp_saved: 'SMS envoyé au numéro enregistré :', otp_send: '📩 Envoyer le code SMS', otp_sending: 'Envoi…',
    otp_code: 'Code à 6 chiffres', otp_verify: '✅ Vérifier & signer', otp_verifying: 'Vérification…', otp_sent: 'SMS envoyé à', otp_6: 'Saisissez le code à 6 chiffres', fout: 'Erreur : '
  }
};
const LANGS = ['nl', 'en', 'de', 'fr'];
const detectLang = (sp) => {
  const q = (sp.get('lang') || '').toLowerCase();
  if (LANGS.includes(q)) return q;
  try { const saved = localStorage.getItem('womsys_sign_lang'); if (LANGS.includes(saved)) return saved; } catch (_) {}
  const nav = (navigator.language || 'nl').toLowerCase().slice(0, 2);
  return LANGS.includes(nav) ? nav : 'en';
};

const SignPage = () => {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const [lang, setLang] = useState(() => detectLang(sp));
  const t = T[lang] || T.nl;
  const switchLang = (l) => { setLang(l); try { localStorage.setItem('womsys_sign_lang', l); } catch (_) {} };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);     // { state, message, signer }
  const [pages, setPages] = useState([]);
  const [showSignModal, setShowSignModal] = useState(false);
  const [showWeigerModal, setShowWeigerModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [doneMessage, setDoneMessage] = useState(null);
  // AES Premium / OTP states
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [akkoord, setAkkoord] = useState(false); // FORMS_V2
  const [pendingSignData, setPendingSignData] = useState(null);
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpRequesting, setOtpRequesting] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpMessage, setOtpMessage] = useState('');

  const canvasRefs = useRef({});

  // Laad signer info + PDF
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/public/${token}`);
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          throw new Error(data.message || `Token ongeldig (HTTP ${r.status})`);
        }
        const data = await r.json();
        if (cancelled) return;
        setInfo(data);

        // Alleen PDF laden als status open is
        const closedStates = ['compleet', 'geweigerd', 'reeds_getekend', 'reeds_geweigerd', 'verlopen', 'nog_niet_aan_de_beurt'];
        if (closedStates.includes(data.state)) {
          setLoading(false);
          return;
        }

        // PDF laden
        const pdfRes = await fetch(`${API}/public/${token}/pdf`);
        if (!pdfRes.ok) throw new Error(`PDF kon niet worden geladen (HTTP ${pdfRes.status})`);
        const buf = await pdfRes.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
        if (cancelled) return;

        const info_ = [];
        for (let p = 1; p <= pdf.numPages; p++) {
          const page = await pdf.getPage(p);
          const viewport = page.getViewport({ scale: RENDER_SCALE });
          info_.push({ pageNum: p, width: viewport.width, height: viewport.height, page, viewport });
        }
        if (!cancelled) {
          setPages(info_);
          setLoading(false);
        }
      } catch (err) {
        console.error('SignPage laad-fout:', err);
        if (!cancelled) {
          setError(err.message || 'Onbekende fout');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Render canvases
  useEffect(() => {
    pages.forEach(({ pageNum, page, viewport }) => {
      const canvas = canvasRefs.current[pageNum];
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      page.render({ canvasContext: ctx, viewport }).promise.catch(e =>
        console.error(`Pagina ${pageNum} render fout:`, e)
      );
    });
  }, [pages]);

  // Tekenen — TYPED_NAME_V2
  const handleSign = async (dataUrl, getypteNaam) => {
    if (signer && signer.has_name_field) {
      if (!getypteNaam || getypteNaam.trim().length < 1) {
        alert(t.naam_req);
        return;
      }
    }
    setSubmitting(true);
    try {
      const body = { handtekening_data: dataUrl, akkoord: true };
      if (getypteNaam && getypteNaam.trim().length > 0) {
        body.getypte_naam = getypteNaam.trim();
      }
      const r = await fetch(`${API}/public/${token}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await r.json();
      if (!r.ok || !data.success) {
        if (data.requires_otp) {
          setPendingSignData({ dataUrl, getypteNaam });
          setShowSignModal(false);
          setShowOtpModal(true);
          setSubmitting(false);
          return;
        }
        throw new Error(data.message || `HTTP ${r.status}`);
      }
      setShowSignModal(false);
      setDoneMessage(data.compleet ? t.s_done_all : t.s_done);
    } catch (err) {
      alert(t.teken_fout + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // OTP handlers voor AES Premium
  const handleRequestOtp = async () => {
    // Phone check verwijderd: backend gebruikt opgeslagen signer.telefoon als phone leeg is
    setOtpRequesting(true);
    setOtpMessage('');
    try {
      const r = await fetch(`${API}/public/${token}/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone })
      });
      const data = await r.json();
      if (!data.success) throw new Error(data.message);
      setOtpRequested(true);
      setOtpMessage(`${t.otp_sent} ${data.phone_masked}`);
    } catch (e) {
      setOtpMessage(t.fout + e.message);
    } finally {
      setOtpRequesting(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otpCode.length !== 6) { setOtpMessage(t.otp_6); return; }
    setOtpVerifying(true);
    try {
      const r = await fetch(`${API}/public/${token}/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: otpCode })
      });
      const data = await r.json();
      if (!data.success) throw new Error(data.message);
      setShowOtpModal(false);
      setOtpRequested(false);
      setOtpCode('');
      setOtpMessage('');
      if (pendingSignData) {
        const { dataUrl, getypteNaam } = pendingSignData;
        setPendingSignData(null);
        await handleSign(dataUrl, getypteNaam);
      }
    } catch (e) {
      setOtpMessage(t.fout + e.message);
    } finally {
      setOtpVerifying(false);
    }
  };

  // Weigeren
  const handleWeiger = async (reden) => {
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/public/${token}/weigeren`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reden })
      });
      const data = await r.json();
      if (!r.ok || !data.success) throw new Error(data.message || `HTTP ${r.status}`);
      setShowWeigerModal(false);
      setDoneMessage(t.w_done);
    } catch (err) {
      alert(t.w_fout + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Render ----
  if (loading) return <CenteredMessage icon="⏳" title={t.loading} />;

  if (error) return (
    <CenteredMessage
      icon="⚠️"
      title={t.invalid}
      subtitle={error}
    />
  );

  if (doneMessage) return (
    <CenteredMessage
      icon="✅"
      title={t.done}
      subtitle={doneMessage}
    />
  );

  // Closed states (al getekend / al geweigerd / workflow compleet of geweigerd)
  const closed = ['compleet', 'geweigerd', 'reeds_getekend', 'reeds_geweigerd', 'verlopen', 'nog_niet_aan_de_beurt'].includes(info.state);
  if (closed) return (
    <CenteredMessage
      icon={info.state === 'compleet' || info.state === 'reeds_getekend' ? '✅' : info.state === 'nog_niet_aan_de_beurt' ? '⏳' : '⚠️'}
      title={
        info.state === 'verlopen'        ? t.st_verlopen :
        info.state === 'nog_niet_aan_de_beurt' ? t.st_beurt :
        info.state === 'compleet'        ? t.st_compleet :
        info.state === 'reeds_getekend'  ? t.st_reeds :
        info.state === 'geweigerd'       ? t.st_geweigerd :
                                            t.st_reeds_gew
      }
      subtitle={info.message}
    />
  );

  // Open state — render PDF + signer info + acties
  const signer = info.signer;
  return (
    <div style={pageWrap}>
      {/* Header */}
      <div style={header}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '12px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: 'white', marginBottom: 4, flex: 1 }}>
              {signer.template_naam}
            </div>
            <div style={{ display: 'flex', gap: 2, background: 'rgba(255,255,255,0.15)', borderRadius: 6, padding: 2 }}>
              {LANGS.map(l => (
                <button key={l} type="button" onClick={() => switchLang(l)}
                  style={{ padding: '3px 8px', fontSize: 12, fontWeight: 700, border: 'none', borderRadius: 4, cursor: 'pointer', background: lang === l ? 'white' : 'transparent', color: lang === l ? '#1e3a5f' : 'white' }}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)' }}>
            {t.hi} <strong>{signer.signer_naam}</strong> — {t.intro}
            {signer.bedrijf_naam && <> {t.verzoek} <strong>{signer.bedrijf_naam}</strong>.</>}
          </div>
        </div>
      </div>

      {/* PDF preview */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px' }}>
        {pages.map(({ pageNum, width, height }) => (
          <div key={pageNum} style={{ marginBottom: 16 }}>
            <div style={pageBadge}>{t.pagina} {pageNum}</div>
            <div style={{
              position: 'relative', width, height,
              margin: '4px auto', maxWidth: '100%',
              boxShadow: '0 2px 12px rgba(0,0,0,0.12)', borderRadius: 4,
              overflow: 'hidden', background: 'white'
            }}>
              <canvas
                ref={el => { canvasRefs.current[pageNum] = el; }}
                width={width} height={height}
                style={{ display: 'block', width: '100%', height: 'auto' }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Sticky bottom bar */}
      <div style={bottomBar}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', gap: 12, alignItems: 'center', padding: '12px 20px' }}>
          <label style={{ flex: 1, fontSize: 13, color: '#334155', display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={akkoord} onChange={e => setAkkoord(e.target.checked)} style={{ marginTop: 2, width: 18, height: 18 }} />
            <span>{t.akkoord} <strong>{t.akkoord2}</strong>{t.akkoord3}</span>
          </label>
          <button onClick={() => setShowWeigerModal(true)} disabled={submitting} style={btnDanger}>
            {t.weigeren}
          </button>
          <button onClick={() => setShowSignModal(true)} disabled={submitting || !akkoord} title={akkoord ? '' : t.vink}
            style={{ ...btnPrimary, opacity: akkoord ? 1 : 0.45, cursor: akkoord ? 'pointer' : 'not-allowed' }}>
            {t.ondertekenen}
          </button>
        </div>
      </div>

      {/* Modals */}
      {showSignModal && (
        <SignatureModal
          label={`${t.handtekening} — ${signer.signer_naam}`}
          signer={signer}
          t={t}
          onSave={handleSign}
          onClose={() => setShowSignModal(false)}
          submitting={submitting}
        />
      )}
      {showOtpModal && (
        <OtpModal
          phone={phone}
          setPhone={setPhone}
          phoneMasked={info?.signer_telefoon_masked || info?.signer?.signer_telefoon_masked}
          otpCode={otpCode}
          setOtpCode={setOtpCode}
          otpRequested={otpRequested}
          otpRequesting={otpRequesting}
          otpVerifying={otpVerifying}
          otpMessage={otpMessage}
          t={t}
          onRequestOtp={handleRequestOtp}
          onVerifyOtp={handleVerifyOtp}
          onClose={() => {
            setShowOtpModal(false);
            setPendingSignData(null);
            setOtpRequested(false);
            setOtpCode('');
            setOtpMessage('');
          }}
        />
      )}
      {showWeigerModal && (
        <WeigerModal
          t={t}
          onSave={handleWeiger}
          onClose={() => setShowWeigerModal(false)}
          submitting={submitting}
        />
      )}
    </div>
  );
};

// ===== OTP Modal voor AES Premium =====
const OtpModal = ({ phone, setPhone, otpCode, setOtpCode, otpRequested, otpRequesting, otpVerifying, otpMessage, onRequestOtp, onVerifyOtp, onClose, phoneMasked, t = T.nl }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
    <div style={{ background: 'white', padding: 32, borderRadius: 12, maxWidth: 440, width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <span style={{ fontSize: 32 }}>📱</span>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, color: '#1f2937' }}>{t.otp_title}</h2>
          <small style={{ color: '#6b7280' }}>{t.otp_sub}</small>
        </div>
      </div>
      {!otpRequested ? (
        <>
          <p style={{ color: '#374151', fontSize: 14, marginBottom: 16 }}>{t.otp_p}</p>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' }}>{t.otp_tel}</label>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t.otp_tel_ph} autoFocus
            style={{ width: '100%', padding: 12, fontSize: 16, border: '1px solid #d1d5db', borderRadius: 6, marginBottom: 4, boxSizing: 'border-box' }} />
          {phoneMasked && (
            <p style={{ fontSize: 12, color: '#059669', margin: '0 0 12px 0' }}>📲 {t.otp_saved} <strong>{phoneMasked}</strong></p>
          )}
          {otpMessage && <p style={{ color: '#dc2626', fontSize: 13, margin: '4px 0 12px 0' }}>{otpMessage}</p>}
          <button onClick={onRequestOtp} disabled={otpRequesting}
            style={{ width: '100%', padding: 14, fontSize: 16, fontWeight: 600, background: otpRequesting ? '#9ca3af' : '#f59e0b', color: 'white', border: 'none', borderRadius: 6, cursor: otpRequesting ? 'wait' : 'pointer' }}>
            {otpRequesting ? t.otp_sending : t.otp_send}
          </button>
          <button onClick={onClose} style={{ width: '100%', padding: 10, fontSize: 13, marginTop: 8, background: 'transparent', color: '#6b7280', border: 'none', cursor: 'pointer' }}>{t.annuleren}</button>
        </>
      ) : (
        <>
          <p style={{ color: '#059669', fontSize: 14, fontWeight: 600, marginBottom: 16 }}>✅ {otpMessage}</p>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' }}>{t.otp_code}</label>
          <input type="text" inputMode="numeric" maxLength={6} value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000" autoFocus
            style={{ width: '100%', padding: 14, fontSize: 28, fontWeight: 700, letterSpacing: 12, textAlign: 'center', border: '1px solid #d1d5db', borderRadius: 6, marginBottom: 12, fontFamily: 'monospace', boxSizing: 'border-box' }} />
          {otpMessage && /^(Fout|Error|Fehler|Erreur)/.test(otpMessage) && <p style={{ color: '#dc2626', fontSize: 13, margin: '4px 0 12px 0' }}>{otpMessage}</p>}
          <button onClick={onVerifyOtp} disabled={otpVerifying || otpCode.length !== 6}
            style={{ width: '100%', padding: 14, fontSize: 16, fontWeight: 600, background: otpVerifying ? '#9ca3af' : '#10b981', color: 'white', border: 'none', borderRadius: 6, cursor: otpVerifying ? 'wait' : (otpCode.length === 6 ? 'pointer' : 'not-allowed'), opacity: otpCode.length === 6 ? 1 : 0.5 }}>
            {otpVerifying ? t.otp_verifying : t.otp_verify}
          </button>
          <button onClick={onClose} style={{ width: '100%', padding: 10, fontSize: 13, marginTop: 8, background: 'transparent', color: '#6b7280', border: 'none', cursor: 'pointer' }}>{t.annuleren}</button>
        </>
      )}
    </div>
  </div>
);

// ===== Centered message helper =====
const CenteredMessage = ({ icon, title, subtitle }) => (
  <div style={{
    minHeight: '100vh', background: '#f8fafc',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24
  }}>
    <div style={{
      maxWidth: 440, background: 'white', borderRadius: 12,
      padding: 32, textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
    }}>
      <div style={{ fontSize: 56, marginBottom: 12 }}>{icon}</div>
      <h1 style={{ margin: '0 0 8px', fontSize: 22, color: '#1e3a5f' }}>{title}</h1>
      {subtitle && <p style={{ margin: 0, color: '#64748b', fontSize: 14, lineHeight: 1.5 }}>{subtitle}</p>}
    </div>
  </div>
);

// ===== Signature canvas modal =====
const SignatureModal = ({ label, signer, onSave, onClose, submitting, t = T.nl }) => {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  // TYPED_NAME_INPUT_V2: signer typt zelf zijn naam als template een naam-vakje heeft
  const needsName = !!(signer && signer.has_name_field);
  const [getypteNaam, setGetypteNaam] = useState(signer?.signer_naam || '');

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (cx - rect.left) * sx, y: (cy - rect.top) * sy };
  };

  const start = (e) => {
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath(); ctx.moveTo(x, y);
    ctx.lineWidth = 3; ctx.lineCap = 'round'; ctx.strokeStyle = '#1e293b';
    setDrawing(true);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y); ctx.stroke();
  };
  const end = () => { if (drawing) { setDrawing(false); setHasContent(true); } };

  const clear = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasContent(false);
  };
  const save = () => {
    if (!hasContent) { alert(t.eerst); return; }
    if (needsName && (!getypteNaam || getypteNaam.trim().length < 1)) {
      alert(t.naam_req);
      return;
    }
    onSave(canvasRef.current.toDataURL('image/png'), needsName ? getypteNaam.trim() : null);
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{ ...modalBox, maxWidth: 700 }} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700 }}>{t.m_title}</h2>
        <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b' }}>
          {label} — {t.m_sub}
        </p>
        {needsName && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#1e293b' }}>
              {t.m_naam} <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              type="text"
              value={getypteNaam}
              onChange={(e) => setGetypteNaam(e.target.value)}
              placeholder={t.m_ph}
              maxLength={200}
              autoFocus
              style={{
                width: '100%', padding: 10,
                border: '1px solid #cbd5e1', borderRadius: 6,
                fontSize: 14, boxSizing: 'border-box'
              }}
            />
          </div>
        )}
        <canvas
          ref={canvasRef} width={700} height={250}
          style={{
            width: '100%', height: 250, border: '2px dashed #cbd5e1',
            borderRadius: 8, background: '#fafafa', touchAction: 'none', display: 'block'
          }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button onClick={clear} style={btnSecondary} disabled={submitting}>{t.wissen}</button>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={btnSecondary} disabled={submitting}>{t.annuleren}</button>
          <button onClick={save} disabled={submitting} style={btnPrimary}>
            {submitting ? t.bezig : t.opslaan}
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== Weigerings modal =====
const WeigerModal = ({ onSave, onClose, submitting, t = T.nl }) => {
  const [reden, setReden] = useState('');
  const valid = reden.trim().length >= 3;
  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalBox} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700 }}>{t.w_title}</h2>
        <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b' }}>
          {t.w_sub}
        </p>
        <textarea
          value={reden} onChange={(e) => setReden(e.target.value)}
          placeholder={t.w_ph} rows={4}
          style={{ width: '100%', padding: 10, border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, boxSizing: 'border-box', resize: 'vertical' }}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnSecondary} disabled={submitting}>{t.annuleren}</button>
          <button
            onClick={() => valid && onSave(reden.trim())}
            disabled={!valid || submitting}
            style={{ ...btnDanger, opacity: (!valid || submitting) ? 0.5 : 1 }}
          >
            {submitting ? t.bezig : t.w_btn}
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== Styles =====
const pageWrap = { minHeight: '100vh', background: '#f1f5f9', paddingBottom: 90 };
const header = { background: '#1e3a5f', position: 'sticky', top: 0, zIndex: 10 };
const pageBadge = { display: 'inline-block', padding: '3px 10px', background: '#1e3a5f', color: 'white', fontSize: 11, fontWeight: 600, borderRadius: 4, marginBottom: 4 };
const bottomBar = { position: 'fixed', bottom: 0, left: 0, right: 0, background: 'white', borderTop: '1px solid #e2e8f0', boxShadow: '0 -2px 12px rgba(0,0,0,0.06)', zIndex: 20 };
const btnPrimary = { padding: '10px 18px', border: 'none', background: '#10b981', color: 'white', borderRadius: 8, fontSize: 14, cursor: 'pointer', fontWeight: 600 };
const btnSecondary = { padding: '10px 16px', border: '1px solid #cbd5e1', background: 'white', borderRadius: 6, fontSize: 14, cursor: 'pointer', fontWeight: 500 };
const btnDanger = { padding: '10px 16px', border: 'none', background: '#dc2626', color: 'white', borderRadius: 8, fontSize: 14, cursor: 'pointer', fontWeight: 600 };
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 };
const modalBox = { background: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 480, boxShadow: '0 20px 50px rgba(0,0,0,0.3)' };

export default SignPage;
